import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Bell, Gift, Star, ShoppingBag, Gavel, Users, Download, Megaphone, Trash2, Clock3 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const ICONS = {
  welcome: Gift, review_completed: Star, artwork_purchased: ShoppingBag,
  auction_sold: Gavel, auction_listed: Gavel, referral_reward: Users, withdrawal: Download,
  announcement: Megaphone, auction_open: Gavel, auction_eligible: Clock3, auction_ineligible: Clock3,
  auction_reminder: Clock3,
};

export default function NotificationBell() {
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const seenAnn = useRef(null);

  const load = () => api.get("/notifications").then((r) => {
    setData(r.data);
    const anns = (r.data.items || []).filter((n) => n.type === "announcement");
    if (seenAnn.current === null) {
      // baseline on first load — don't toast existing announcements
      seenAnn.current = new Set(anns.map((a) => a.id));
    } else {
      anns.forEach((a) => {
        if (!seenAnn.current.has(a.id)) {
          seenAnn.current.add(a.id);
          toast(a.title, { description: a.body, icon: "📣" });
        }
      });
    }
  }).catch(() => {});

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onOpen = async (o) => {
    setOpen(o);
    if (o && data.unread > 0) {
      await api.post("/notifications/read-all").catch(() => {});
      setData((d) => ({ ...d, unread: 0 }));
    }
  };

  const clearAll = async () => {
    try {
      await api.post("/notifications/bulk-delete", { all: true });
      seenAnn.current = new Set();
      setData({ items: [], unread: 0 });
      toast.success("All notifications cleared");
    } catch {
      toast.error("Couldn't clear notifications. Please try again.");
      load();
    }
  };

  const deleteOne = async (id) => {
    try {
      await api.post("/notifications/bulk-delete", { ids: [id] });
      setData((d) => ({
        items: d.items.filter((n) => n.id !== id),
        unread: d.items.find((n) => n.id === id && !n.read) ? Math.max(0, d.unread - 1) : d.unread,
      }));
    } catch {
      toast.error("Couldn't delete notification. Please try again.");
      load();
    }
  };

  return (
    <Popover open={open} onOpenChange={onOpen}>
      <PopoverTrigger asChild>
        <button data-testid="notif-bell" aria-label="Notifications" className="relative h-10 w-10 grid place-items-center rounded-full hover:bg-secondary transition-colors">
          <Bell className="h-5 w-5" />
          {data.unread > 0 && (
            <span data-testid="notif-badge" className="absolute top-1.5 right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-gold text-gold-foreground text-[10px] font-bold grid place-items-center">
              {data.unread > 9 ? "9+" : data.unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0 rounded-2xl" data-testid="notif-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <span className="font-heading font-bold">Notifications</span>
          {data.items.length > 0 && (
            <button onClick={clearAll} data-testid="notif-clear-all"
              className="flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-destructive transition-colors">
              <Trash2 className="h-3.5 w-3.5" /> Clear all
            </button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {data.items.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">No notifications yet.</p>
          ) : data.items.map((n) => {
            const Icon = ICONS[n.type] || Bell;
            return (
              <div key={n.id} data-testid={`notif-item-${n.id}`} className={`px-4 py-3 border-b border-border last:border-0 flex gap-3 group ${!n.read ? "bg-gold/5" : ""}`}>
                <div className="h-9 w-9 rounded-full bg-secondary grid place-items-center shrink-0"><Icon className="h-4 w-4 text-gold" /></div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold">{n.title}</p>
                  <p className="text-xs text-muted-foreground">{n.body}</p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">{new Date(n.created_at).toLocaleString()}</p>
                </div>
                <button onClick={() => deleteOne(n.id)} data-testid={`notif-delete-${n.id}`} aria-label="Delete notification"
                  className="shrink-0 h-7 w-7 grid place-items-center rounded-full text-muted-foreground hover:text-destructive hover:bg-secondary transition-colors opacity-60 group-hover:opacity-100">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
